import { useTranslation } from 'react-i18next';
import type { TourDefinition } from '@klonup/design-system';
import { createTaskSteps } from './createTaskSteps';

/**
 * Tour del detalle de una conexión para BackupManager. Tiene dos tramos encadenados:
 *
 * 1. La pantalla (intro → tasks → team): presenta la conexión, sus tareas y su equipo.
 * 2. Crear una tarea (new-task → … → done): el paso `new-task` usa `advanceOn` para avanzar
 *    cuando el usuario pulsa de verdad el botón "Nueva tarea" (no con "Siguiente"), lo que
 *    abre el ConnectionTaskSplitPanel. Los pasos siguientes apuntan a las secciones del panel —
 *    que solo existen tras abrirlo: el MutationObserver del motor las localiza al montarse y
 *    `scrollIntoView` (ya en el motor) las trae a la vista en un panel scrollable.
 *
 * Sin `requiredPath`: las anclas viven en la ruta dinámica `/connections/:id`, a la que OrgLayout no
 * puede navegar sin saber qué conexión abrir. Por eso el tour se deshabilita en el menú fuera
 * del detalle de conexión y se auto-dispara por evento al entrar en él (ver OrgLayout).
 */
export function useBackupManagerConnectionDetailTour(): TourDefinition {
  const { t } = useTranslation();
  return {
    id: 'bm-connection-detail',
    title: t('onboarding.bm_connection_detail.menuTitle'),
    description: t('onboarding.bm_connection_detail.menuDescription'),
    icon: '🔄',
    steps: [
      {
        id: 'intro',
        // Sin target → se renderiza como modal centrado de introducción.
        title: t('onboarding.bm_connection_detail.steps.intro.title'),
        description: t('onboarding.bm_connection_detail.steps.intro.description'),
      },
      {
        id: 'tasks',
        target: '[data-tour="connection-tasks"]',
        title: t('onboarding.bm_connection_detail.steps.tasks.title'),
        description: t('onboarding.bm_connection_detail.steps.tasks.description'),
      },
      {
        id: 'team',
        target: '[data-tour="connection-team"]',
        title: t('onboarding.bm_connection_detail.steps.team.title'),
        description: t('onboarding.bm_connection_detail.steps.team.description'),
      },
      // Tramo "crear tarea" — compartido con el tour del Admin (DRY, ver createTaskSteps).
      ...createTaskSteps(t, 'onboarding.bm_connection_detail.steps'),
    ],
  };
}
